/**
 * Assessments Tab — RN Admission / Supervisory Visit / Re-Admission assessments
 * completed on the agency's own PDF forms. Replaces the old supervisory log,
 * which stays viewable underneath.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import StartAssessmentDialog from "@/components/assessments/StartAssessmentDialog";
import AssessmentFiller from "@/components/assessments/AssessmentFiller";
import AssessmentList from "@/components/assessments/AssessmentList";
import { DueBadge, ScheduledInfo } from "@/components/assessments/DuePanel";
import ScheduleVisitDialog from "@/components/assessments/ScheduleVisitDialog";
import { fmtDate } from "@/components/assessments/assessmentMeta";
import { CalendarPlus, Play, Plus, Stethoscope } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AssessmentsTab({ clientId, clientName, onCompleted }) {
  const { user } = useAuth();
  const canEdit = ["rn", "admin"].includes((user?.role || "").toLowerCase());
  const [items, setItems] = useState(null);
  const [starting, setStarting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [due, setDue] = useState([]);
  const [startType, setStartType] = useState("admission");
  const [scheduling, setScheduling] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/assessments`, { params: { client_id: clientId, group: "assessments" } });
      setItems(r.data.assessments.filter((a) => a.assessment_type !== "supervisory")); // supervisory has its own tab
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load assessments");
      setItems([]);
    }
    axios.get(`${API}/assessments/due`, { params: { client_id: clientId } }).then((r) => setDue((r.data.items || []).filter((i) => i.kind === "readmission"))).catch(() => setDue([]));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const closeFiller = useCallback(() => setOpenId(null), []);

  return (
    <div data-testid="client-assessments-tab" className="space-y-6">
      <div className="form-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-rose-600" /> RN Assessments ({items?.length ?? 0})
            </h3>
            <p className="text-sm text-slate-500">
              Admission and Re-Admission assessments on your agency's forms. Supervisory visits have their own tab.
            </p>
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => { setStartType("admission"); setStarting(true); }} className="gap-2 bg-rose-600 hover:bg-rose-700" data-testid="new-assessment-btn">
              <Plus className="w-4 h-4" /> New assessment
            </Button>
          )}
        </div>

        {due.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-2 mb-4" data-testid="client-due-banners">
            {due.map((it) => {
              const tone = it.status === "overdue" ? "border-red-200 bg-red-50 text-red-900" : it.status === "due_soon" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-700";
              return (
                <div key={it.kind} className={`rounded-lg border px-3 py-2 flex flex-wrap items-center gap-2 text-sm ${tone}`} data-testid={`client-due-${it.kind}`}>
                  <div className="flex-1 min-w-[160px]">
                    <p className="font-medium">{it.kind === "supervisory" ? "Supervisory visit" : "Re-admission"} due {fmtDate(it.due_date)}</p>
                    <p className="text-xs opacity-80">Last: {fmtDate(it.last_date)} · every {it.interval_days} days{it.kind === "readmission" && it.insurance_company ? ` (${it.insurance_company})` : ""}</p>
                  </div>
                  <DueBadge item={it} />
                  <ScheduledInfo item={it} />
                  {canEdit && !it.scheduled_appointment && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setScheduling(it)} data-testid={`client-due-schedule-${it.kind}`}>
                      <CalendarPlus className="w-3 h-3" /> Schedule
                    </Button>
                  )}
                  {canEdit && it.status !== "ok" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setStartType(it.kind); setStarting(true); }} data-testid={`client-due-start-${it.kind}`}>
                      <Play className="w-3 h-3" /> Start
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <AssessmentList
          items={items}
          canEdit={canEdit}
          onOpen={(a) => setOpenId(a.id)}
          onChanged={load}
          emptyText={canEdit ? "No assessments yet — click “New assessment” to start an Admission or Re-Admission." : "No assessments recorded yet."}
        />
      </div>

      <StartAssessmentDialog open={starting} onClose={() => setStarting(false)} clientId={clientId} clientName={clientName} defaultType={startType} onStarted={(a) => { load(); setOpenId(a.id); }} />
      <ScheduleVisitDialog item={scheduling} onClose={() => setScheduling(null)} onScheduled={load} />
      {openId && <AssessmentFiller assessmentId={openId} onClose={closeFiller} onCompleted={() => { load(); onCompleted?.(); }} />}
    </div>
  );
}
